import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Loader2, Search, Filter } from "lucide-react";
import BuyerRequestCard from "@/components/BuyerRequestCard";
import { getSupabaseClient, isSupabaseInitialized } from "@/integrations/supabase/client";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { toast } from "@/components/ui/use-toast";
import { getSafeClient } from "@/integrations/supabase/safeClient";

// Request types that match the BuyerRequestCard component props
type BuyerRequest = {
  id: string;
  niche: string;
  drMin: number;
  daMin: number;
  budgetMin: number;
  budgetMax: number;
  linkType: string;
  notes: string;
  expiresAt: string;
  createdAt: string;
  userId: string;
  userName: string | null;
  userAvatar: string | null;
};

const BuyerRequests = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // Increased to show more per page in grid layout

  // Fetch buyer requests from Supabase
  const { data, isLoading, error } = useQuery({
    queryKey: ["buyerRequests", currentPage, activeCategory, searchQuery],
    queryFn: async (): Promise<{ requests: BuyerRequest[]; count: number }> => {
      try {
        if (!isSupabaseInitialized()) {
          console.error("Supabase client not initialized in BuyerRequests component");
          toast({
            title: "Connection Error",
            description: "Database connection issue. Try refreshing the page.",
            variant: "destructive"
          });
          throw new Error("Database connection not initialized");
        }
        
        console.log("Supabase client status:", isSupabaseInitialized() ? "initialized" : "not initialized");
        console.log("Using Supabase client:", await getSupabaseClient());
        
        // First, fetch the buyer requests without joins
        let query = (await getSupabaseClient())
          .from("buyer_requests")
          .select('*', { count: 'exact' })
          .order("created_at", { ascending: false })
          .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

        // Add category filter if not "all"
        if (activeCategory !== "all") {
          query = query.eq("niche", activeCategory);
        }

        // Add search query if exists
        if (searchQuery) {
          query = query.or(`niche.ilike.%${searchQuery}%, notes.ilike.%${searchQuery}%`);
        }

        console.log("BuyerRequests: Executing query...");
        const { data, error, count } = await query;

        if (error) {
          console.error("Error fetching buyer requests:", error);
          console.error("Error details:", JSON.stringify(error));
          toast({
            title: "Error",
            description: "Failed to load buyer requests",
            variant: "destructive",
          });
          throw error;
        }
        
        if (!data || data.length === 0) {
          return { requests: [], count: 0 };
        }
        
        console.log("BuyerRequests: Data received:", data.length, "items");
        
        // Transform data without profile info first
        const transformedData: BuyerRequest[] = data.map(item => ({
          id: item.id,
          niche: item.niche,
          drMin: item.dr_min || 0,
          daMin: item.da_min || 0,
          budgetMin: item.budget_min_cents ? item.budget_min_cents / 100 : 0,
          budgetMax: item.budget_max_cents ? item.budget_max_cents / 100 : 0,
          linkType: item.link_type || '',
          notes: item.notes || '',
          expiresAt: item.expires_at,
          createdAt: item.created_at,
          userId: item.user_id,
          userName: "Loading...",
          userAvatar: null
        }));
        
        // Get unique user IDs to fetch profiles separately
        const userIds = [...new Set(data.map(item => item.user_id))];
        
        // Fetch profiles if there are user IDs
        if (userIds.length > 0) {
          try {
            const { data: profilesData, error: profilesError } = await (await getSupabaseClient())
              .from('profiles')
              .select('id, name, avatar_url')
              .in('id', userIds);
              
            if (profilesError) {
              console.error('Error fetching profiles:', profilesError);
            } else if (profilesData) {
              // Create a lookup map for quick profile access
              const profilesMap = profilesData.reduce((acc, profile) => {
                acc[profile.id] = profile;
                return acc;
              }, {} as Record<string, any>);
              
              // Update requests with profile data
              transformedData.forEach(request => {
                const profile = profilesMap[request.userId];
                if (profile) {
                  request.userName = profile.name || "Anonymous";
                  request.userAvatar = profile.avatar_url;
                } else {
                  request.userName = "Anonymous";
                }
              });
            }
          } catch (profileError) {
            console.error('Error processing profiles:', profileError);
          }
        }

        return { requests: transformedData, count: count || transformedData.length };
      } catch (err) {
        console.error("Error in query function:", err);
        console.error("Error stack:", err instanceof Error ? err.stack : "No stack available");
        throw err; // Re-throw to let React Query handle retries and error states
      }
    },
    retry: 3, // Retry 3 times if the query fails
    retryDelay: attempt => Math.min(attempt > 1 ? 2000 : 1000, 30 * 1000), // Increasing delay between retries
  });

  // Calculate total pages
  const totalPages = Math.ceil((data?.count || 0) / itemsPerPage);

  // Sample categories - replace with actual categories from your database
  const categories = [
    { id: "all", name: "All Categories" },
    { id: "technology", name: "Technology" },
    { id: "health", name: "Health & Fitness" },
    { id: "finance", name: "Finance" },
    { id: "education", name: "Education" },
    { id: "travel", name: "Travel" },
    { id: "food", name: "Food & Cooking" },
  ];

  return (
    <>
      <Helmet>
        <title>Browse Buyer Requests | BackLink Hub</title>
      </Helmet>
      <div className="container mx-auto py-8 px-4 md:px-6">
        <h1 className="text-3xl font-bold mb-2">Buyer Requests</h1>
        <p className="text-muted-foreground mb-6">
          Browse requests from buyers looking for quality backlinks
        </p>

        {/* Search and Filter Section */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative w-full md:w-1/2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by keyword or description..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" className="flex items-center gap-2 w-full md:w-auto">
            <Filter className="h-4 w-4" />
            Filter Options
          </Button>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-6">
          <TabsList className="overflow-x-auto flex w-full justify-start p-0 h-auto">
            {categories.map((category) => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                className="px-4 py-2 focus:ring-0"
              >
                {category.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Separator className="mb-6" />

        {/* Requests List */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading requests...</span>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-red-500">Error loading requests. Please try again later.</p>
            </CardContent>
          </Card>
        ) : data?.requests?.length === 0 ? (
          <EmptyState
            title="No requests found"
            description="Try adjusting your search or filters, or check back later"
            icon="inbox"
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
              {data?.requests.map((request) => (
                <BuyerRequestCard 
                  key={request.id}
                  id={request.id}
                  niche={request.niche}
                  drMin={request.drMin}
                  daMin={request.daMin}
                  budgetMin={request.budgetMin}
                  budgetMax={request.budgetMax}
                  linkType={request.linkType}
                  notes={request.notes}
                  expiresAt={request.expiresAt}
                  createdAt={request.createdAt}
                  userId={request.userId}
                  userName={request.userName}
                  userAvatar={request.userAvatar}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </>
  );
};

export default BuyerRequests;
